// components/dashboard/SalesChart.tsx
"use client";

import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface SalesChartProps {
  data: { date: string; sales: number; orders: number }[];
}

export function SalesChart({ data }: SalesChartProps) {
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && theme === "dark";

  const colors = {
    primary: "#0f7a43",       // سبز برند
    grid: isDark ? "#1e3a2a" : "#e2ece3",
    axis: isDark ? "#94a3b8" : "#6f8377",
    tooltipBg: isDark ? "#0f2018" : "#ffffff",
    tooltipBorder: isDark ? "#1e3a2a" : "#e2ece3",
    tooltipText: isDark ? "#e5e7eb" : "#122a1c",
  };

  const fontStyle = {
    fontFamily: "var(--font-yekan-bakh), system-ui, Tahoma, sans-serif",
    fontSize: 12,
  };

  return (
    <div className="materio-card h-full flex flex-col">
      <div className="p-5 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-foreground">
              نمودار فروش هفتگی
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              مجموع فروش ۷ روز اخیر
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-xs text-muted-foreground">فروش</span>
          </div>
        </div>
      </div>
      <div className="p-5 pt-0 flex-1">
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={colors.primary}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={colors.primary}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={colors.grid}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tick={{ ...fontStyle, fill: colors.axis }}
              axisLine={false}
              tickLine={false}
              dy={10}
            />
            <YAxis
              tick={{ ...fontStyle, fill: colors.axis }}
              tickFormatter={(v) => `${(v / 1000000).toFixed(0)}م`}
              axisLine={false}
              tickLine={false}
              dx={-10}
            />
            <Tooltip
              cursor={{
                stroke: colors.primary,
                strokeWidth: 1,
                strokeDasharray: "5 5",
              }}
              formatter={(value: number) => [
                `${value.toLocaleString("fa-IR")} تومان`,
                "فروش",
              ]}
              contentStyle={{
                fontFamily:
                  "var(--font-yekan-bakh), system-ui, Tahoma, sans-serif",
                borderRadius: "12px",
                border: `1px solid ${colors.tooltipBorder}`,
                backgroundColor: colors.tooltipBg,
                color: colors.tooltipText,
                boxShadow: "0 4px 20px rgba(8,60,32,0.12)",
                direction: "rtl",
                fontSize: 13,
                padding: "8px 12px",
              }}
              labelStyle={{
                fontFamily:
                  "var(--font-yekan-bakh), system-ui, Tahoma, sans-serif",
                fontWeight: "bold",
                color: colors.primary,
                marginBottom: 4,
              }}
            />
            <Area
              type="monotone"
              dataKey="sales"
              stroke={colors.primary}
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorSales)"
              animationDuration={1800}
              animationEasing="ease-out"
              activeDot={{
                r: 6,
                strokeWidth: 3,
                stroke: colors.primary,
                fill: colors.tooltipBg,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}